import { Button, Grid2, IconButton, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { useQuery } from "@tanstack/react-query";
import { useAppContext } from "AppContext";
import { GKClientDataGrid, StatusField } from "components";
import { DeleteIcon } from "components/Icons";
import dayjs from "dayjs";
import { useState } from "react";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import axiosInstance from "utils/axiosInstance";
import { extraColumnsField } from "utils/helpers";
import AddCorporateActionModal from "./AddCorporateActionModal";

function CorporateActions({ companyDetails }: any) {
  const { id } = useParams();
  const [isOpenCorporateAction, setIsOpenCorporateACtion] = useState(undefined);

  const {
    state: { currentUser },
  } = useAppContext();

  const isDisabled = currentUser?.phone === "9911223344";

  const { data, isFetching, refetch } = useQuery({
    queryKey: ["ADMIN_INSTRUMENT_CA"],
    queryFn: async () => axiosInstance.get(`/admin-instrument-ca/${id}/`),
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Something went wrong");
    },
    refetchInterval: 60 * 1000,
  });

  const corporateColumns: GridColDef[] = [
    {
      ...extraColumnsField("Date", "date", 80, 1),
      valueGetter: (row) => dayjs(row?.row?.date).format("DD/MM/YYYY"),
    },
    {
      ...extraColumnsField("Action Type", "type", 80, 1),
    },
    {
      ...extraColumnsField("Details", "details", 350, 1),
      renderCell: (row) => {
        return (
          <>
            {row?.row?.type === "dividend" && (
              <Typography>
                <b>Dividend Per Share:</b> {row?.row?.dividendPerShare}
              </Typography>
            )}

            {(row?.row?.type === "split" ||
              row?.row?.type === "bonus" ||
              row?.row?.type === "reverse_split") && (
              <Grid2 container>
                <Grid2 size={12}>
                  <Typography>
                    <b>For Stocks: </b>
                    {row?.row?.forStocks}
                  </Typography>
                </Grid2>
                <Grid2 size={12}>
                  <Typography>
                    <b>Issued Stocks:</b> {row?.row?.issuedStocks}
                  </Typography>
                </Grid2>
              </Grid2>
            )}

            {row?.row?.type === "demerger" && (
              <Grid2 container>
                <Grid2 size={12}>
                  <Typography>
                    <b>Existing Asset Quantity: </b>
                    {row?.row?.existingAssetQuantity}
                  </Typography>
                </Grid2>
                <Grid2 size={12}>
                  <Typography>
                    <b>New Asset Quantity:</b>
                    {row?.row?.newAssetQuantity}
                  </Typography>
                </Grid2>
                <Grid2 size={12}>
                  <Typography>
                    <b>Cost Acquisition Ratio:</b>
                    {row?.row?.costAcquisitionRatio}
                  </Typography>
                </Grid2>
                {row?.row?.demergedCompanies?.map((company: any) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <Grid2 container>
                    <Grid2 size={12}>
                      <Typography>
                        <b>Demerged Company:</b>
                        {company.childCompany}
                      </Typography>
                    </Grid2>
                    <Grid2 size={12}>
                      <Typography className="flex flex-row gap-2">
                        <b>New Asset Quantity:</b>
                        {company.newAssetQuantity}
                      </Typography>
                    </Grid2>
                    <Grid2 size={12}>
                      <Typography className="flex flex-row gap-2">
                        <b>Cost Acquisition Ratio:</b>
                        {company.costAcquisitionRatio}
                      </Typography>
                    </Grid2>
                  </Grid2>
                ))}
              </Grid2>
            )}

            {row?.row?.type === "merger" && (
              <Grid2 container>
                <Grid2 size={12}>
                  <b>Existing Asset Quantity:</b>
                  {row?.row?.existingAssetQuantity}
                </Grid2>
                <Grid2 size={12}>
                  <b>New Asset Quantity:</b>
                  {row?.row?.newAssetQuantity}
                </Grid2>
                <Grid2 size={12}>
                  <b>Parent Company:</b>
                  {row?.row?.parentCompany}
                </Grid2>
              </Grid2>
            )}

            {row?.row?.type === "right_issue" && (
              <Grid2 container>
                <Grid2 size={12}>
                  <Typography>
                    <b>For Stocks: </b>
                    {row?.row?.forStocks}
                  </Typography>
                </Grid2>
                <Grid2 size={12}>
                  <Typography>
                    <b>Issued Stocks:</b> {row?.row?.issuedStocks}
                  </Typography>
                </Grid2>
                <Grid2 size={12}>
                  <b>premium:</b>
                  {row?.row?.premium}
                </Grid2>
                <Grid2 size={12}>
                  <b>Right Issue Instrument:</b>
                  {row?.row?.reGcode}
                </Grid2>
              </Grid2>
            )}

            {row?.row?.type === "REC" && (
              <Grid2 container>
                <Grid2 size={12}>
                  <b>premium:</b>
                  {row?.row?.premium}
                </Grid2>
                <Grid2 size={12}>
                  <b>closingDate:</b>
                  {dayjs(row?.row?.closingDate).format("DD/MM/YYYY")}
                </Grid2>
                <Grid2 size={12}>
                  <b>Patly Paid Instrument:</b>
                  {row?.row?.reGcode}
                </Grid2>
              </Grid2>
            )}
          </>
        );
      },
    },
    {
      ...extraColumnsField("Purpose", "purpose", 150, 1),
      valueGetter: (params) => params?.row?.purpose || "-",
    },
    {
      ...extraColumnsField("Ceil", "isCeil", 150, 1),
      renderCell: (params) => (
        <StatusField statusString={params?.row?.isCeil ? "True" : "False"} />
      ),
    },
    {
      ...extraColumnsField("Action", "action", 50, 1),
      align: "right",
      headerAlign: "right",
      renderCell: (params) => {
        return (
          <IconButton
            disabled={isDisabled}
            onClick={async () => {
              await axiosInstance
                .delete(`/admin-instrument-ca/${id}/delete`, {
                  params: {
                    ca_id: params.row.caId,
                    act_type: params.row.type,
                  },
                })
                .then((res) => {
                  toast.success(res.data.message);
                  refetch();
                })
                .catch((error) => {
                  toast.error(
                    error?.response?.data?.message || "Something went wrong"
                  );
                });
            }}
          >
            <DeleteIcon width={20} height={20} />
          </IconButton>
        );
      },
    },
  ];

  return (
    <Grid2 container>
      <Grid2 size={12} mb={2} display={"flex"} justifyContent={"end"}>
        <Button
          variant="contained"
          disabled={isDisabled}
          size="medium"
          onClick={() => setIsOpenCorporateACtion(!isOpenCorporateAction)}
        >
          Add Corporate Action
        </Button>
      </Grid2>
      <Grid2 size={12}>
        <GKClientDataGrid
          loading={isFetching}
          columns={corporateColumns}
          showFilters={false}
          rows={
            data?.data?.data?.map((data: any, index: number) => {
              return {
                ...data,
                id: index,
              };
            }) || []
          }
          rowHeight={150}
        />
      </Grid2>

      {isOpenCorporateAction && (
        <AddCorporateActionModal
          isModalOpen={isOpenCorporateAction}
          setIsModalOpen={setIsOpenCorporateACtion}
          refetch={refetch}
          companyDetails={companyDetails}
        />
      )}
    </Grid2>
  );
}

export default CorporateActions;
